import dayjs from 'dayjs'
import localeDate from 'dayjs/plugin/localeData'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import ElementLocale from 'element-ui/lib/locale'
import { includes } from 'lodash'
import Vue from 'vue'
import VueI18n from 'vue-i18n'

dayjs.extend(relativeTime)
dayjs.extend(localeDate)
dayjs.extend(localizedFormat)
dayjs.extend(quarterOfYear)
dayjs.extend(utc)
Vue.use(VueI18n)
const loadedLanguages: I18nLang[] = [] // 我们的预装默认语言

export type I18nLang = 'en' | 'cn' | 'jp'

/**
 * 界面语言兜底语言。日文语言包尚未翻译完整,缺失的 key 需要回退到英文而不是暴露 key 名
 */
const FALLBACK_LANG: I18nLang = 'en'

const DAYJS_LOCALE_MAP: Record<I18nLang, string> = {
  cn: 'zh-cn',
  en: 'en',
  jp: 'ja',
}

/**
 * 界面语言对应的 html lang 属性值,需要是合法的 BCP 47 标签
 */
const HTML_LANG_MAP: Record<I18nLang, string> = {
  cn: 'zh-CN',
  en: 'en',
  jp: 'ja',
}

const SUPPORTED_LANGS: I18nLang[] = ['cn', 'en', 'jp']

async function importLangMessage(lang: I18nLang) {
  // 因为在workspace微前端有繁体等未支持语言,所以这里需要判断一下
  if (!includes(SUPPORTED_LANGS, lang)) {
    return {}
  }
  const langMessage = await import(/* webpackChunkName: "lang-biz" */ `./messages/biz-${lang}`)
  return langMessage.default
}

/**
 * 非兜底语言需要同时装载兜底语言包,否则 fallbackLocale 无 messages 可回退
 */
async function loadMessagesWithFallback(lang: I18nLang) {
  if (lang === FALLBACK_LANG) {
    return { [lang]: await importLangMessage(lang) }
  }
  const [langMessage, fallbackMessage] = await Promise.all([importLangMessage(lang), importLangMessage(FALLBACK_LANG)])
  return {
    [FALLBACK_LANG]: fallbackMessage,
    [lang]: langMessage,
  }
}

export let i18n!: VueI18n
export async function createI18n(locale: I18nLang = 'en') {
  if (i18n) {
    return i18n
  }

  const messages = await loadMessagesWithFallback(locale)
  i18n = new VueI18n({
    locale: locale, // 设置语言环境
    fallbackLocale: FALLBACK_LANG,
    messages,
    silentTranslationWarn: true,
  })
  ElementLocale.i18n((key: string, value: string) => i18n.t(key, value))
  dayjs.locale(DAYJS_LOCALE_MAP[locale] || DAYJS_LOCALE_MAP[FALLBACK_LANG])
  loadedLanguages.push(...Object.keys(messages).map((lang) => lang as I18nLang))
  return i18n
}

export function setI18nLanguage(lang: I18nLang) {
  i18n.locale = lang
  dayjs.locale(DAYJS_LOCALE_MAP[lang] || DAYJS_LOCALE_MAP[FALLBACK_LANG])

  document.querySelector('html')?.setAttribute('lang', HTML_LANG_MAP[lang] || HTML_LANG_MAP[FALLBACK_LANG])
  return lang
}

export async function loadLanguageAsync(lang: I18nLang) {
  if (!i18n) {
    return createI18n(lang)
  }
  // 如果语言相同
  if (i18n.locale === lang) {
    return i18n
  }

  // 如果语言已经加载
  if (loadedLanguages.includes(lang)) {
    setI18nLanguage(lang)
    return i18n
  }

  // 如果尚未加载语言
  const messages = await loadMessagesWithFallback(lang)
  Object.entries(messages).forEach(([messageLang, message]) => {
    if (loadedLanguages.includes(messageLang as I18nLang)) {
      return
    }
    i18n.setLocaleMessage(messageLang, message)
    loadedLanguages.push(messageLang as I18nLang)
  })
  setI18nLanguage(lang)
  return i18n
}
